import { useEffect } from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { COPY, LICENSE_URL } from "@/constants/copy";
import { CONTACT } from "@/constants/contact";

const linkCls = "font-semibold text-navy underline decoration-dotted underline-offset-4 hover:text-terracotta transition-colors";

const NOT_DONE = [
  "Plumbing beyond caulking and simple fixture swaps",
  "Electrical work like new circuits and whole-house wiring",
  "Foundation work",
  "Full remodels and additions",
  "Roofing and gutters",
];
const NOT_SURE = "Not sure? Book the $50 visit or send a photo and I'll tell you straight.";

// `text` is the plain answer (also published as FAQPage schema); `rich` overrides what's shown.
const FAQS = [
  { q: "What does the $50 estimate visit include?",
    text: "I come out, look at the job, and give you straight advice and an honest estimate on the spot. If you hire me, the full $50 is credited to your job. (Plus WA sales tax on the visit.)" },
  { q: "What do you charge for the work?", text: COPY.rates },
  { q: "Why is there a 2-hour minimum?",
    text: "Every work visit means loading the truck with the right tools and materials, driving out, setting up and cleaning up when I'm done. The 2-hour minimum covers that, so even a small job gets done right instead of rushed. Tip: save up a few small fixes and I'll take care of them in the same visit." },
  { q: "What is the $50 trip charge?",
    text: "A flat $50 for each day I come out to work at your home. It covers drive time and fuel. A one-day job has one trip charge; a job that takes three days has three." },
  { q: "When are estimate visits?", text: "Weekdays, 4–5 PM. Book online and pick the day that works for you. Can't make 4–5 PM? Request a custom time in the booking form and we'll make it happen." },
  { q: "How will I know my visit is confirmed?",
    text: "I'll confirm your visit by email, usually within one business day, Mon–Fri 9–5. You'll also get a reminder the morning of your visit. For multi-day jobs, you'll get a 'Please confirm your dates' email with a link. Tap it to lock in your dates.",
    rich: () => <>
      <span className="block">I'll confirm your visit by email, usually within one business day, Mon–Fri 9–5. You'll also get a reminder the morning of your visit.</span>
      <span className="block mt-2">For multi-day jobs, you'll get a 'Please confirm your dates' email with a link. Tap it to lock in your dates.</span>
    </> },
  { q: "I've hired you before. Do I pay the $50 again?",
    text: "No. Returning customers skip the estimate fee. Use Continue my project to request your next visit.",
    rich: (onBook) => <>No. Returning customers skip the estimate fee.{" "}
      <button type="button" data-testid="faq-continue-project" data-book-cta onClick={() => onBook(null, "work")} className={linkCls}>
        Continue my project →
      </button></> },
  { q: "What does the lifetime guarantee cover?",
    text: "My workmanship, for as long as you own the home. If something I built, repaired or installed fails because of how I did the work, I come back and fix it free. Materials and products are covered by their manufacturer's warranty." },
  { q: "Are you licensed?",
    text: `${COPY.trust}. WA License #SOLIDHS742NQ — verify it with the state at Washington L&I.`,
    rich: () => <>{COPY.trust}.{" "}
      <a href={LICENSE_URL} target="_blank" rel="noreferrer" data-testid="faq-license-link" className={linkCls}>
        WA License #SOLIDHS742NQ
      </a>{" "}— verify it with the state.</> },
  { q: "Do you have employees or subcontractors?",
    text: "No. It's just me, Bryan. No employees, no subcontractors, no hand-offs: the person who gives you the estimate is the person who does the work. If your project outgrows one pair of hands, I'll point you to the right local tradespeople." },
  { q: "What's your service area?",
    text: "Poulsbo, Kingston, Bainbridge Island, Hansville, Silverdale, Bremerton, Suquamish and all of North Kitsap County." },
  { q: "What don't you do?",
    text: `I don't take on: ${NOT_DONE.join("; ")}. ${NOT_SURE}`,
    rich: (onBook) => <>
      <ul className="list-disc pl-5 space-y-1" data-testid="faq-not-done-list">
        {NOT_DONE.map((s) => <li key={s}>{s}</li>)}
      </ul>
      <span className="block mt-3" data-testid="faq-not-sure">
        Not sure?{" "}
        <button type="button" data-testid="faq-not-sure-book" data-book-cta onClick={() => onBook(null)} className={linkCls}>
          Book the $50 visit
        </button>{" "}or{" "}
        <a href={CONTACT.smsHref} data-testid="faq-not-sure-photo" className={linkCls}>send a photo</a>{" "}
        and I'll tell you straight.
      </span>
    </> },
];

export const FAQ_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.text } })),
});

// Build-time prerender: keep every answer in the HTML (hidden until opened) so crawlers read it.
const SSR = typeof window === "undefined";

// One FAQPage block in <head> while the FAQ is on the page (home + town pages).
const useFaqSchema = () => useEffect(() => {
  const el = document.getElementById("faq-jsonld") || document.createElement("script");
  el.id = "faq-jsonld";
  el.type = "application/ld+json";
  el.text = FAQ_JSONLD;
  document.head.appendChild(el);
  return () => el.remove();
}, []);

export const Faq = ({ onBook }) => {
  useFaqSchema();
  return (
    <section id="faq" data-testid="faq-section" className="py-16 sm:py-24 border-t border-border">
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl grid grid-cols-12 gap-y-8 gap-x-4 md:gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="col-span-12 lg:col-span-4"
        >
          <p className="overline-label mb-4">FAQ</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
            Straight answers.
          </h2>
        </motion.div>
        <Accordion type="single" collapsible className="col-span-12 lg:col-span-8 border-t border-border" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-border">
              <AccordionTrigger data-testid={`faq-question-${i}`}
                className="py-5 font-display text-base sm:text-lg font-bold text-navy hover:no-underline hover:text-terracotta transition-colors gap-4 text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent {...(SSR ? { forceMount: true, hidden: true } : {})} data-testid={`faq-answer-${i}`} className="text-base text-muted-foreground leading-relaxed pb-5 pr-2 sm:pr-8">
                {f.rich ? f.rich(onBook) : f.text}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
